import { Button } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useContext, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

import { AddEditPageContext } from 'features/inventory/page/contexts/AddEditPageContext'
import { Page } from 'features/inventory/types/page'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useAbility } from 'providers/casl'
import { browserRelativeRedirect } from 'providers/router/utils'
import { mergeCloseAction } from 'providers/snackbar/mergeCloseAction'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { mapGraphQLErrors } from 'providers/tanstack-query/utils'
import { ROUTES } from 'routes'
import { AddSchema } from '../../schemas/addSchema'
import { CREATE_PAGE, Data, Variables } from './api'
import { PageForSnackbar, UseCreatePage } from './types'

export const useCreatePage = (): UseCreatePage => {
  const ability = useAbility()
  const { workspaceId } = useWorkspaceParam()
  const navigate = useNavigate()
  const { t } = useTranslation('features/page')
  const { t: tCommon } = useTranslation('common')
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading } = useContext(AddEditPageContext)
  const [errors, setErrors] = useState<FieldErrors<AddSchema>>({})

  const { mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: CREATE_PAGE(ability),
    onCompleted: async (data: Data) => {
      const {
        createPage: { errors, page },
      } = data

      setLoading(false)

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('form.error.create'), { variant: 'error' })
      } else if (page) {
        setErrors({})
        navigate(generatePath(ROUTES.INVENTORY.PAGE.LIST, { workspaceId }))
        enqueueSuccessSnackbar(page)
      }
    },
  })

  const enqueueSuccessSnackbar = (page: PageForSnackbar): void => {
    const { id, name } = page

    enqueueSnackbar(t('form.success.create', { pageName: name }), {
      action: snackbarId =>
        mergeCloseAction(
          <Button
            color='white'
            onClick={() => handleClickEdit(id)}
          >
            {tCommon('form.action.edit').toUpperCase()}
          </Button>,
          snackbarId,
        ),
      variant: 'success',
    })
  }

  const handleClickEdit = (id: Page['id']): void =>
    browserRelativeRedirect(
      generatePath(ROUTES.INVENTORY.PAGE.EDIT, {
        id,
        tab: 'general',
        workspaceId,
      }),
    )

  const createPage = (data: AddSchema): void => {
    setLoading(true)

    mutateAsync({
      input: data,
    })
  }

  return { createPage, errors }
}
